'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConversionCTAFloatProps {
  threshold?: number;
  position?: 'bottom-right' | 'bottom-left' | 'bottom-center';
}

const ConversionCTAFloat = ({ 
  threshold = 60, 
  position = 'bottom-right' 
}: ConversionCTAFloatProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollProgress = (scrollTop / docHeight) * 100;
      
      setIsVisible(scrollProgress >= threshold);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [threshold]);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offsetTop = element.offsetTop - 80;
      window.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }
  };

  const getPositionClasses = () => {
    switch (position) {
      case 'bottom-left':
        return 'bottom-6 left-6';
      case 'bottom-center':
        return 'bottom-6 left-1/2 transform -translate-x-1/2';
      default:
        return 'bottom-6 right-6';
    }
  };

  if (!isVisible) return null;

  return (
    <div data-component-id="src/components/common/ConversionCTAFloat.tsx:56:4" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="56" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`fixed ${getPositionClasses()} z-300 animate-fade-in`}>
      <div data-component-id="src/components/common/ConversionCTAFloat.tsx:57:6" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="57" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22relative%20group%22%7D" className="relative group">
        {/* Pulse Animation Ring */}
        <div data-component-id="src/components/common/ConversionCTAFloat.tsx:59:8" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="59" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-inset-1%20bg-accent%20rounded-full%20opacity-30%20animate-ping%22%7D" className="absolute -inset-1 bg-accent rounded-full opacity-30 animate-ping" />
        
        {/* Main CTA Button */}
        <button data-component-id="src/components/common/ConversionCTAFloat.tsx:62:8" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="62" data-component-file="ConversionCTAFloat.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22relative%20bg-accent%20hover%3Abg-accent%2F90%20text-accent-foreground%20p-4%20rounded-full%20shadow-cta%20hover%3Ashadow-lg%20transition-all%20duration-250%20hover%3Ascale-110%20group%22%7D"
          onClick={scrollToContact}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          className="relative bg-accent hover:bg-accent/90 text-accent-foreground p-4 rounded-full shadow-cta hover:shadow-lg transition-all duration-250 hover:scale-110 group"
          aria-label="Book consultation"
        >
          <Icon data-component-id="src/components/common/ConversionCTAFloat.tsx:69:10" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="69" data-component-file="ConversionCTAFloat.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22PhoneIcon%22%2C%22className%22%3A%22transition-transform%20duration-250%20group-hover%3Ascale-110%22%7D" 
            name="PhoneIcon" 
            size={24} 
            className="transition-transform duration-250 group-hover:scale-110" 
          />
          
          {/* Tooltip */}
          <div data-component-id="src/components/common/ConversionCTAFloat.tsx:76:10" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="76" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22textContent%22%3A%22Book%20Consultation%22%7D" className={`absolute bottom-full right-0 mb-2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap transition-all duration-250 ${
            isHovered ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2 pointer-events-none'
          }`}>
            Book Consultation
            <div data-component-id="src/components/common/ConversionCTAFloat.tsx:80:12" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="80" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20right-4%20w-0%20h-0%20border-l-4%20border-r-4%20border-t-4%20border-transparent%20border-t-gray-900%22%7D" className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900" />
          </div>
        </button>

        {/* Secondary Action - Only on Desktop */}
        <div data-component-id="src/components/common/ConversionCTAFloat.tsx:85:8" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="85" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22hidden%20lg%3Ablock%20absolute%20-top-16%20right-0%22%7D" className="hidden lg:block absolute -top-16 right-0">
          <button data-component-id="src/components/common/ConversionCTAFloat.tsx:86:10" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="86" data-component-file="ConversionCTAFloat.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22bg-white%20hover%3Abg-muted%20text-text-primary%20p-3%20rounded-full%20shadow-card%20hover%3Ashadow-lg%20transition-all%20duration-250%20hover%3Ascale-105%20border%20border-border%22%7D"
            onClick={() => {
              const element = document.getElementById('services');
              if (element) {
                const offsetTop = element.offsetTop - 80;
                window.scrollTo({
                  top: offsetTop,
                  behavior: 'smooth',
                });
              }
            }}
            className="bg-white hover:bg-muted text-text-primary p-3 rounded-full shadow-card hover:shadow-lg transition-all duration-250 hover:scale-105 border border-border"
            aria-label="Explore programs"
          >
            <Icon data-component-id="src/components/common/ConversionCTAFloat.tsx:100:12" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="100" data-component-file="ConversionCTAFloat.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22AcademicCapIcon%22%7D" name="AcademicCapIcon" size={20} />
          </button>
        </div>
      </div>

      {/* Urgency Badge */}
      <div data-component-id="src/components/common/ConversionCTAFloat.tsx:106:6" data-component-path="src/components/common/ConversionCTAFloat.tsx" data-component-line="106" data-component-file="ConversionCTAFloat.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20-top-3%20-left-3%20bg-warning%20text-warning-foreground%20text-xs%20font-bold%20px-2%20py-1%20rounded-full%20shadow-card%20animate-pulse%22%2C%22textContent%22%3A%2210%20spots%20left%22%7D" className="absolute -top-3 -left-3 bg-warning text-warning-foreground text-xs font-bold px-2 py-1 rounded-full shadow-card animate-pulse">
        10 spots left
      </div>
    </div>
  );
};

export default ConversionCTAFloat;